import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteModal from '../../components/DeleteModal';
import '../../styles/components/forms.css';

export default function NewSubscription() {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        currency: 'nok',
        tiersMode: true,
        tiers: [{
            amount: '',
            interval: 'month',
            features: []
        }],
        includeMva: false
    });
    const [subscriptions, setSubscriptions] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({
        name: '',
        description: '',
        price: '',
        duration_months: '',
        tiers: [],
        currency: 'nok',
        includeMva: false
    });
    const [editErrors, setEditErrors] = useState({});
    const [editSuccess, setEditSuccess] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [msg, setMsg] = useState('');
    const [subscribers, setSubscribers] = useState([]);
    const [newAppSubscribers, setNewAppSubscribers] = useState([]);

    useEffect(() => {
        fetchSubscriptions();
        fetchSubscribers();
        fetchNewAppSubscribers();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            setSubscriptions(response.data);
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
        }
    };

    const fetchSubscribers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/subscribers`,
                { withCredentials: true }
            );
            setSubscribers(response.data.data);
        } catch (error) {
            console.error('Error fetching subscribers:', error);
        }
    };

    const fetchNewAppSubscribers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/newapp/subscribers`,
                { withCredentials: true }
            );
            setNewAppSubscribers(response.data.data);
        } catch (error) {
            console.error('Error fetching newApp subscribers:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                formData,
                { withCredentials: true }
            );

            if (response.status === 201) {
                setFormData({
                    name: '',
                    description: '',
                    price: '',
                    duration_months: ''
                });
                setMsg('Subscription created successfully');
                await fetchSubscriptions();
            }
        } catch (error) {
            console.error('Error creating subscription:', error);
        }
    };

    const handleEdit = async (id) => {
        if (editingId === id) {
            try {
                setEditErrors({});
                setEditSuccess('');

                // Only include changed fields
                const changedFields = {};
                const subscription = subscriptions.find(s => s.idpricing === id);
                
                if (editData.name !== subscription.name) changedFields.name = editData.name;
                if (editData.description !== subscription.description) changedFields.description = editData.description;
                if (!subscription.tiers_mode && editData.price !== subscription.price) changedFields.price = editData.price;
                
                // Handle tier changes
                if (subscription.tiers_mode && editData.tiers.length > 0) {
                    const changedTiers = editData.tiers.filter((tier, index) => {
                        const originalTier = subscription.tiers[index];
                        return (
                            tier.amount !== originalTier.amount ||
                            tier.payment_interval !== originalTier.payment_interval ||
                            tier.interval_count !== originalTier.interval_count
                        );
                    });
                    
                    if (changedTiers.length > 0) {
                        changedFields.tiers = changedTiers;
                    }
                }

                // Only make the API call if there are changes
                if (Object.keys(changedFields).length > 0) {
                    const response = await axios.put(
                        `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/${id}`,
                        changedFields,
                        { withCredentials: true }
                    );

                    if (response.data.notUpdatedTiers?.length > 0) {
                        setEditErrors({
                            tiers: response.data.notUpdatedTiers.map(tier => ({
                                id: tier.id,
                                message: tier.reason
                            }))
                        });
                    }

                    setEditSuccess('Subscription updated successfully');
                    await fetchSubscriptions();
                } else {
                    setEditSuccess('No changes to update');
                }
            } catch (error) {
                console.error('Error updating subscription:', error);
                setEditErrors({
                    general: error.response?.data?.message || 'Failed to update subscription'
                });
            }
        } else {
            const subscription = subscriptions.find(s => s.idpricing === id);
            if (subscription) {
                // Map tiers to include isUsed flag
                const tiers = subscription.tiers?.map(tier => ({
                    ...tier,
                    isUsed: false // This will be updated when we get the response from the backend
                })) || [];

                setEditData({
                    name: subscription.name || '',
                    description: subscription.description || '',
                    price: subscription.price || '',
                    duration_months: subscription.duration_months || '',
                    tiers,
                    currency: subscription.currency || 'nok',
                    includeMva: subscription.include_mva || false
                });
                setEditingId(id);
                setEditErrors({});
                setEditSuccess('');
            }
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/${id}`,
                { withCredentials: true }
            );
            
            if (response.status === 200) {
                setDeleteConfirm(null);
                await fetchSubscriptions();
            }
        } catch (error) {
            console.error('Error deleting subscription:', error);
        }
    };

    const handleAddTier = () => {
        setFormData(prev => ({
            ...prev,
            tiers: [...prev.tiers, { 
                amount: '',
                interval: 'month',
                features: []
            }]
        }));
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = [...formData.tiers];
        newTiers[index] = { ...newTiers[index], [field]: value };
        setFormData(prev => ({ ...prev, tiers: newTiers }));
    };

    const removeTier = (index) => {
        setFormData(prev => ({
            ...prev,
            tiers: prev.tiers.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </Link>
                <h1>Create New Subscription</h1>
            </div>
            <h1>Create pricing for Stripe Dashboard</h1>
            <form className="form-container" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Subscription Name</label>
                    <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows="4"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="currency">Currency</label>
                    <select
                        id="currency"
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        required
                    >
                        <option value="nok">NOK</option>
                        <option value="usd">USD</option>
                        <option value="eur">EUR</option>
                    </select>
                </div>

                <div className="form-group">
                    <label className="toggle-label">
                        <span>Include MVA</span>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.includeMva}
                                onChange={(e) => setFormData({...formData, includeMva: e.target.checked})}
                            />
                            <span className="toggle-slider"></span>
                        </div>
                    </label>
                </div>

                <div className="form-group">
                    <label className="toggle-label">
                        <span>Enable Tiered Pricing</span>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.tiersMode}
                                onChange={(e) => setFormData({...formData, tiersMode: e.target.checked})}
                            />
                            <span className="toggle-slider"></span>
                        </div>
                    </label>
                </div>

                {formData.tiersMode && (
                    <div className="tiers-section">
                        <button 
                            type="button" 
                            onClick={handleAddTier}
                            className="add-tier-button"
                        >
                            Add Tier
                        </button>

                        {formData.tiers.map((tier, index) => (
                            <div key={index} className="tier-form">
                                <div className="form-group">
                                    <label>Price Amount ({formData.currency.toUpperCase()})</label>
                                    <input
                                        type="number"
                                        value={tier.amount}
                                        onChange={(e) => handleTierChange(index, 'amount', e.target.value)}
                                        placeholder="Enter price amount"
                                        min="0"
                                        step="0.01"
                                        className="tier-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Billing Interval</label>
                                    <select
                                        value={tier.interval}
                                        onChange={(e) => handleTierChange(index, 'interval', e.target.value)}
                                        className="tier-select"
                                    >
                                        <option value="month">Monthly</option>
                                        <option value="6month">6 Months</option>
                                        <option value="year">Yearly</option>
                                    </select>
                                </div>

                                <button 
                                    type="button" 
                                    onClick={() => removeTier(index)}
                                    className="remove-tier-button"
                                >
                                    Remove Tier
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <button type="submit" className="submit-button">Create Subscription</button>
            </form>

            {msg && <div className="success-message">{msg}</div>}

            <div className="subscriptions-list">
                <h2>Existing Pricing</h2>
                <div className="list-container">
                    {subscriptions.map((subscription) => (
                        <div key={subscription.idpricing} className="list-item">
                            {editingId === subscription.idpricing ? (
                                <div className="edit-form">
                                    <div className="form-group">
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            value={editData.name}
                                            onChange={(e) => setEditData({...editData, name: e.target.value})}
                                            className="edit-input"
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={editData.description}
                                            onChange={(e) => setEditData({...editData, description: e.target.value})}
                                            className="edit-textarea"
                                            rows="4"
                                        />
                                    </div>

                                    {subscription.tiers_mode ? (
                                        <div className="tiers-section">
                                            <h4>Pricing Tiers</h4>
                                            {editData.tiers.map((tier, index) => (
                                                <div key={tier.id} className="tier-form">
                                                    <div className="form-group">
                                                        <label>Price Amount ({editData.currency.toUpperCase()})</label>
                                                        <input
                                                            type="number"
                                                            value={tier.amount}
                                                            onChange={(e) => {
                                                                const newTiers = [...editData.tiers];
                                                                newTiers[index] = { ...newTiers[index], amount: e.target.value };
                                                                setEditData({ ...editData, tiers: newTiers });
                                                            }}
                                                            disabled={tier.isUsed}
                                                            className="tier-input"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                        {tier.isUsed && (
                                                            <small className="text-warning">
                                                                This tier has active subscriptions and cannot be edited
                                                            </small>
                                                        )}
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Billing Interval</label>
                                                        <select
                                                            value={tier.payment_interval}
                                                            onChange={(e) => {
                                                                const newTiers = [...editData.tiers];
                                                                newTiers[index] = { 
                                                                    ...newTiers[index], 
                                                                    payment_interval: e.target.value,
                                                                    interval_count: e.target.value === '6month' ? 6 : 1
                                                                };
                                                                setEditData({ ...editData, tiers: newTiers });
                                                            }}
                                                            disabled={tier.isUsed}
                                                            className="tier-select"
                                                        >
                                                            <option value="month">Monthly</option>
                                                            <option value="6month">6 Months</option>
                                                            <option value="year">Yearly</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="form-group">
                                            <label>Price</label>
                                            <input
                                                type="number"
                                                value={editData.price}
                                                onChange={(e) => setEditData({...editData, price: e.target.value})}
                                                className="edit-input"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    )}

                                    {editErrors.general && (
                                        <div className="error-message">{editErrors.general}</div>
                                    )}
                                    {editSuccess && (
                                        <div className="success-message">{editSuccess}</div>
                                    )}

                                    <div className="edit-actions">
                                        <button 
                                            onClick={() => handleEdit(subscription.idpricing)}
                                            className="save-button"
                                        >
                                            Save
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setEditingId(null);
                                                setEditErrors({});
                                                setEditSuccess('');
                                            }}
                                            className="cancel-button"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="subscription-info">
                                        <h3>{subscription.name}</h3>
                                        {subscription.description && (
                                            <p className="subscription-description">{subscription.description}</p>
                                        )}
                                        
                                        {/* Display regular price or tiers */}
                                        {subscription.tiers_mode ? (
                                            <div className="subscription-tiers">
                                                <h4>Tiered Pricing:</h4>
                                                {subscription.tiers && subscription.tiers.length > 0 ? (
                                                    <div className="tiers-list">
                                                        {subscription.tiers.map((tier, index) => (
                                                            <div key={tier.id} className="tier-item">
                                                                <span className="tier-minutes">
                                                                    {tier.max_minutes === -1 ? 
                                                                        'Unlimited activities' : 
                                                                        `Up to ${tier.max_minutes} activities`
                                                                    }
                                                                </span>
                                                                <span className="tier-amount">
                                                                    {subscription.currency?.toUpperCase()} {tier.amount}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p>No tiers defined</p>
                                                )}
                                            </div>
                                        ) : (
                                            subscription.price && (
                                                <p className="subscription-price">
                                                    {subscription.currency?.toUpperCase() || 'NOK'} {subscription.price}
                                                </p>
                                            )
                                        )}
                           
                                        {/* Additional subscription details */}
                                        <div className="subscription-details">
                                            {subscription.payment_interval && (
                                                <p className="subscription-interval">
                                                    Billing: {subscription.payment_interval}ly
                                                </p>
                                            )}
                                            {subscription.include_mva && (
                                                <p className="subscription-mva">Includes MVA</p>
                                            )}
                                            {subscription.stripe_price_id && (
                                                <p className="subscription-stripe-id">
                                                    Stripe Price ID: {subscription.stripe_price_id}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="list-item-actions">
                                        <button
                                            className="edit-button"
                                            onClick={() => {
                                                handleEdit(subscription.idpricing);
                                            }}
                                        >
                                            <i className="fas fa-pencil-alt"></i>
                                        </button>
                                        <button
                                            className="delete-button"
                                            onClick={() => setDeleteConfirm(subscription)}
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {deleteConfirm && (
                <DeleteModal
                    isOpen={!!deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => handleDelete(deleteConfirm.idpricing)}
                    itemName={`subscription "${deleteConfirm.name}"`}
                />
            )}

            <style jsx>{`
                .tier-form {
                    background: #f8f9fa;
                    padding: 20px;
                    border-radius: 8px;
                    margin-bottom: 15px;
                    border: 1px solid #e9ecef;
                }

                .tier-input,
                .tier-select {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    margin-bottom: 5px;
                }

                .tier-input:disabled,
                .tier-select:disabled {
                    background-color: #e9ecef;
                    cursor: not-allowed;
                }

                .form-group small {
                    display: block;
                    color: #6c757d;
                    font-size: 0.875em;
                    margin-top: 4px;
                }

                .text-warning {
                    color: #ffc107;
                }

                .error-message {
                    color: #dc3545;
                    padding: 10px;
                    margin: 10px 0;
                    border: 1px solid #dc3545;
                    border-radius: 4px;
                    background-color: #f8d7da;
                }

                .success-message {
                    color: #28a745;
                    padding: 10px;
                    margin: 10px 0;
                    border: 1px solid #28a745;
                    border-radius: 4px;
                    background-color: #d4edda;
                }

                .edit-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 20px;
                }

                .save-button,
                .cancel-button {
                    padding: 8px 16px;
                    border-radius: 4px;
                    border: none;
                    cursor: pointer;
                    font-weight: 500;
                }

                .save-button {
                    background-color: #28a745;
                    color: white;
                }

                .save-button:hover {
                    background-color: #218838;
                }

                .cancel-button {
                    background-color: #6c757d;
                    color: white;
                }

                .cancel-button:hover {
                    background-color: #5a6268;
                }

                .remove-tier-button {
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 4px;
                    cursor: pointer;
                    margin-top: 10px;
                }

                .remove-tier-button:hover {
                    background-color: #c82333;
                }
            `}</style>
        </div>
    );
} 